<template>
  <div :class="{ messaging: true, fullscreen: fullscreen }">
    <div class="input-area">
      <input ref="chatText" type="text" v-model="chatText" :placeholder="placeholder" :disabled="isLoading" @keypress.enter="queryBot">
      <button @click="queryBot" :disabled="isLoading || !hasChatText" class="send-button" aria-label="Senden">
        <svg aria-hidden="true" focusable="false" viewBox="0 0 512 512"><path fill="currentColor" d="M476 3.2L12.5 270.6c-18.1 10.4-15.8 35.6 2.2 43.2L121 358.4l287.3-253.2c5.5-4.9 13.3 2.6 8.6 8.3L176 407v80.5c0 23.6 28.5 32.9 42.5 15.8L282 426l124.6 52.2c14.2 6 30.4-2.9 33-18.2l72-432C515 7.8 493.3-6.8 476 3.2z"></path></svg>
      </button>
    </div>
    <div class="messaging-history" ref="scrollArea">
      <ol class="bot-dialog">
        <template v-for="(statement, index) in statements">
          <li v-if="statement.type === 'sent-message'"
               :class="{ 'sent-message': true, last: (index + 1 === statements.length || statements[index + 1].type !== 'sent-message') }">{{ statement.message }}</li>
          <li v-if="statement.type === 'text'"
               :class="{ 'bot-reply': true, last: (index + 1 === statements.length || statements[index + 1].type === 'sent-message') }">{{ statement.text }}</li>
          <li v-else-if="statement.type === 'markdown'"
               :class="{ 'bot-reply': true, 'type-markdown': true, last: (index + 1 === statements.length || statements[index + 1].type === 'sent-message') }"
               v-html="parseMarkdown(statement.markdown)"
          >
          <li v-else-if="statement.type === 'image'"
              :class="{ 'bot-reply': true, 'type-image': true, last: (index + 1 === statements.length || statements[index + 1].type === 'sent-message') }">
            <img :src="statement.imageUrl" alt="Foto" />
            <div v-if="statement.caption">{{ statement.caption }}</div>
          </li>
          <li v-else-if="statement.type === 'inlineCallback'"
              :class="{ 'bot-reply': true, 'type-inline-callback': true, last: (index + 1 === statements.length || statements[index + 1].type === 'sent-message') }">
            <div v-if="statement.title" class="inline-callback-title">{{ statement.title }}</div><div class="inline-callback-buttons">
              <template v-for="button in statement.callbackButtons">
                <a v-if="button.type === 'url'" target="_blank" :href="button.url">{{ button.title }}</a>
                <button v-if="button.type === 'callback'" @click="sendCallback(button.payload)">{{ button.title }}</button>
              </template>
            </div>
          </li>
          <li v-if="hasButtons(statement)"
              :class="{ 'button-list': true, 'one-column': statement.sendOptions.buttonsPerRow === 1, 'two-column': statement.sendOptions.buttonsPerRow === 2}">
            <div v-for="button in statement.sendOptions.buttons" class="statement-button">
              <a v-if="button.type === 'url'" target="_blank" :href="button.url">{{ button.title }}</a>
              <button v-if="button.type === 'callback'" @click="sendCallback(button.payload)">{{ button.title }}</button>
            </div>
          </li>
        </template>
      </ol>
      <div v-if="isLoading" class="loading-indicator">
        <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
          <circle cx="84" cy="50" r="10" fill="#6a6a6a">
            <animate attributeName="r" repeatCount="indefinite" dur="0.49019607843137253s" calcMode="spline" keyTimes="0;1" values="10;0" keySplines="0 0.5 0.5 1" begin="0s"></animate>
            <animate attributeName="fill" repeatCount="indefinite" dur="1.9607843137254901s" calcMode="discrete" keyTimes="0;0.25;0.5;0.75;1" values="#6a6a6a;#e2e2e2;#bdbdbd;#979797;#6a6a6a" begin="0s"></animate>
          </circle><circle cx="16" cy="50" r="10" fill="#6a6a6a">
          <animate attributeName="r" repeatCount="indefinite" dur="1.9607843137254901s" calcMode="spline" keyTimes="0;0.25;0.5;0.75;1" values="0;0;10;10;10" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" begin="0s"></animate>
          <animate attributeName="cx" repeatCount="indefinite" dur="1.9607843137254901s" calcMode="spline" keyTimes="0;0.25;0.5;0.75;1" values="16;16;16;50;84" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" begin="0s"></animate>
        </circle><circle cx="50" cy="50" r="10" fill="#979797">
          <animate attributeName="r" repeatCount="indefinite" dur="1.9607843137254901s" calcMode="spline" keyTimes="0;0.25;0.5;0.75;1" values="0;0;10;10;10" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" begin="-0.49019607843137253s"></animate>
          <animate attributeName="cx" repeatCount="indefinite" dur="1.9607843137254901s" calcMode="spline" keyTimes="0;0.25;0.5;0.75;1" values="16;16;16;50;84" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" begin="-0.49019607843137253s"></animate>
        </circle><circle cx="84" cy="50" r="10" fill="#bdbdbd">
          <animate attributeName="r" repeatCount="indefinite" dur="1.9607843137254901s" calcMode="spline" keyTimes="0;0.25;0.5;0.75;1" values="0;0;10;10;10" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" begin="-0.9803921568627451s"></animate>
          <animate attributeName="cx" repeatCount="indefinite" dur="1.9607843137254901s" calcMode="spline" keyTimes="0;0.25;0.5;0.75;1" values="16;16;16;50;84" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" begin="-0.9803921568627451s"></animate>
        </circle><circle cx="16" cy="50" r="10" fill="#e2e2e2">
          <animate attributeName="r" repeatCount="indefinite" dur="1.9607843137254901s" calcMode="spline" keyTimes="0;0.25;0.5;0.75;1" values="0;0;10;10;10" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" begin="-1.4705882352941175s"></animate>
          <animate attributeName="cx" repeatCount="indefinite" dur="1.9607843137254901s" calcMode="spline" keyTimes="0;0.25;0.5;0.75;1" values="16;16;16;50;84" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" begin="-1.4705882352941175s"></animate>
        </circle>
        </svg>
      </div>
    </div>
  </div>
</template>
<script>
  import "whatwg-fetch";
  import {mapState} from "vuex";
  import * as marked from "marked";
  const {botUrl} = STADTKATALOG_CONFIG;

  function getUUID() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0,
        v = c === "x"
          ? r
          : (r & 0x3 | 0x8)
      ;
      return v.toString(16);
    });
  }

  export default {
    name: "bot",
    props: {
      fullscreen: {
        type: Boolean,
        required: false
      },
    },
    data: function() {
      return {
        userId: null,
        isLoading: false,
        isOnboarding: false,
        chatText: "",
        statements: [],
      };
    },
    computed: {
      ...mapState([
      ]),
      hasChatText: function() {
        return this.chatText.trim() !== "";
      },
      placeholder: function () {
        return this.$mq === "mobile"
          ? "Schreibe eine Nachricht …"
          : "Schreibe dem Seestadt.bot eine Nachricht …";
      }
    },
    methods: {
      addStatements(statements) {
        this.statements = this.statements.concat(statements);
        if (localStorage) {
          localStorage.setItem("bot-stored-statements", JSON.stringify(this.statements.slice(-10) || []));
        }
      },
      updateUI() {
        this.isLoading = false;
        this.chatText = "";
        this.$refs.chatText.focus();
        this.$nextTick(() => {
          this.$refs.scrollArea.scrollTop = this.$refs.scrollArea.scrollHeight;
        });
      },
      sendText(text) {
        let waitForOnboarding = this.isOnboarding;
        if (!waitForOnboarding) {
          this.chatText = text;
          this.$refs.chatText.focus();
        }

        setTimeout(() => {
          if (waitForOnboarding) {
            this.chatText = text;
            this.$refs.chatText.focus();
          }

          if (this.chatText === text) {
            this.queryBot();
          }
        }, ((waitForOnboarding ? 3250 : 700) + (Math.random() * 200)));
      },
      queryBot() {
        if (!this.hasChatText) {
          return;
        }
        this.isLoading = true;
        this.$nextTick(() => {
          this.$refs.scrollArea.scrollTop = this.$refs.scrollArea.scrollHeight;
        });
        this.statements.push({
          type: "sent-message",
          message: this.chatText,
        });
        fetch(botUrl,{
          method: "POST",
          mode: "cors",
          cache: "no-cache",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: this.userId,
            type: "text",
            text: this.chatText.trim(),
          })
        })
          .then(function (response) {
            if(response.ok) {
              return response.json();
            }

            throw new Error("Network error.");
          })
          .then((json) => {
            this.addStatements(json);
          }).catch(() => {
            this.addStatements([
              { type: "text",text: "😓 Leider kann der Bot gerade nicht abgefragt werden.",sendOptions: { textFormat: "plain"}}
            ]);
          }).finally(() => {
            this.updateUI();
          });
      },
      sendCallback(payload) {
        this.isLoading = true;
        this.$nextTick(() => {
          this.$refs.scrollArea.scrollTop = this.$refs.scrollArea.scrollHeight;
        });
        fetch(botUrl,{
          method: "POST",
          mode: "cors",
          cache: "no-cache",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: this.userId,
            type: "callback",
            text: payload,
          })
        })
          .then(function (response) {
            if(response.ok) {
              return response.json();
            }

            throw new Error("Network error.");
          })
          .then((json) => {
            this.addStatements(json);
          })
          .finally(() => {
            this.updateUI();
          });
      },
      parseMarkdown(md) {
        return marked(md);
      },
      hasButtons(statement) {
        return statement.sendOptions
          && Array.isArray(statement.sendOptions.buttons)
          && statement.sendOptions.buttons.length > 0;
      },
      onboardUser() {
        this.isLoading = true;
        this.isOnboarding = true;
        this.$nextTick(() => {
          this.$refs.scrollArea.scrollTop = this.$refs.scrollArea.scrollHeight;
        });
        setTimeout(() => {
          this.addStatements(
            [
              {
                "type": "markdown",
                "markdown": "**Willkommen beim Seestadt.bot!**\nDu kannst hier mir mit chatten. Soll ich dir meine Funktionen erklären?",
                "sendOptions": {
                  "textFormat": "markdown",
                  "buttons": [
                    {"type":"callback", "title": "💡 Funktionen zeigen", "payload": "hilfe"},
                  ]
                }
              }
            ]
          );

          if (this.fullscreen === true) {
            this.isLoading = true;
            this.$nextTick(() => {
              this.$refs.scrollArea.scrollTop = this.$refs.scrollArea.scrollHeight;
            });
            setTimeout(() => {
              this.addStatements(
                [
                  {
                    "type": "markdown",
                    "markdown": "Du benutzt eine Chat-App? Dann findest du mich auch hier:",
                    "sendOptions": {
                      "textFormat": "markdown",
                      "buttons": [
                        {"type":"url", "title": "In Facebook Messenger öffnen", "url": "https://m.me/SeestadtBot"},
                        {"type":"url", "title": "In Telegram öffnen", "url": "https://telegram.me/SeestadtBot"},
                        {"type":"callback", "title": "❓ Hilfe", "payload": "hilfe"},
                      ]
                    }
                  }
                ]
              );
              this.updateUI();
              this.isOnboarding = false;
            }, 1150);
          } else {
            this.updateUI();
            this.isOnboarding = false;
          }
        }, 2000);
      }
    },
    created() {
      if (localStorage) {
        const storedUserId = localStorage.getItem("bot-user-id");
        if (!storedUserId) {
          this.userId = getUUID();
          localStorage.setItem("bot-user-id", this.userId);
        } else {
          this.userId = storedUserId;
        }

        // restore last statements
        try {
          const storedStatments = JSON.parse(localStorage.getItem("bot-stored-statements") || "[]");
          if (Array.isArray(storedStatments)) {
            this.statements = storedStatments;
          }
        } catch (e) {
          console.error(`Could not restore statements. ${e}`);
        }
      } else {
        this.userId = getUUID();
      }

      console.log("This client has the user id '" + this.userId + "'");
    },
    mounted() {
      this.$refs.scrollArea.scrollTop = this.$refs.scrollArea.scrollHeight;
      this.$refs.chatText.focus();

      // to improve onboarding, create some artificial dialog
      if (localStorage) {
        if (localStorage.getItem("bot-stored-statements") === null) {
          this.onboardUser();
        }
      }
    }
  }
</script>
<style lang="scss">
  @import "../assets/seestadtbot/siteVariables";
  @import "~sass-mq";

  $input-area-height: 100px;
  $dialog-margin: 35px;
  $bubble-padding: 15px;

  .messaging {
    display: flex;
    flex-direction: column-reverse;
    height: 100%;

    .input-area {
      display: flex;
      height: $input-area-height;
      padding: #{$input-area-height / 4} 8px;

      @include mq($from: tablet) {
        padding: #{$input-area-height / 4} $dialog-margin;
      }

      input {
        font-family: $font-text;
        font-weight: 300;
      }

      input[type="text"] {
        outline: none;
        display: inline-block;
        width: 100%;
        padding: #{$input-area-height / 8} #{$input-area-height / 4};
        height: #{$input-area-height / 2};
        border-radius: #{$input-area-height / 4} 0 0 #{$input-area-height / 4};
        border: 1px solid #ccc;
        border-right: none;
        background: #f7f7f7;
      }
    }

    .send-button {
      -webkit-appearance: none;
      outline: none;
      display: inline-flex;
      align-items: center;
      justify-content: space-around;
      width: 80px;
      height: #{$input-area-height / 2};
      padding-right: 14px;
      border-radius: 0 #{$input-area-height / 4} #{$input-area-height / 4} 0;
      border: 1px solid #ccc;
      background: #d0d79f;
      text-align: center;
      cursor: pointer;

      svg {
        width: auto;
        height: 60%;

        color: #6d742c;
      }

      &:disabled svg {
        opacity: 0.25;
      }
    }

    .messaging-history {
      height: calc(100% - #{$input-area-height});
      overflow-x: hidden;
      overflow-y: auto;
    }
  }

  .loading-indicator {
    padding: 10px 35px;

    svg {
      width: 2rem;
    }
  }

  .bot-dialog {
    display: flex;
    flex-direction: column;
    padding: 5px 0 0 0;
    margin: 5px $dialog-margin;

    li {
      padding: 8px $bubble-padding;
      margin-top: 5px;
      margin-bottom: 5px;
      display: inline-block;

      @include mq($from: tablet) {
        max-width: 75%;
      }
    }

    .sent-message,
    .bot-reply {
      font-size: 1rem;
      line-height: 1.375rem;

      &:not(.type-markdown) {
        white-space: pre-wrap;
      }
      border-radius: 20px;

      &.type-markdown {
        strong {
          font-weight: 700;
        }
        p {
          margin: 0;
          white-space: pre-wrap;
        }
        p + p {
          margin: 1rem 0;
        }
      }

      &.type-image {
        padding-top: 15px;

        img {
          border: 1px solid #888;
          border-radius: 5px;
          max-width: 100%;
          box-shadow: 0 0 3px #999a9f;
        }
      }
    }

    .bot-reply {
      align-self: flex-start;
      background-color: #eee;
      position: relative;

      &.last:before {
        content: "";
        position: absolute;
        z-index: 0;
        bottom: 0;
        left: -7px;
        height: 20px;
        width: 20px;
        background: #eee;
        border-bottom-right-radius: 15px;
      }
      &.last:after {
        content: "";
        position: absolute;
        z-index: 1;
        bottom: 0;
        left: -10px;
        width: 10px;
        height: 20px;
        background: white;
        border-bottom-right-radius: 10px;
      }
    }

    .sent-message {
      align-self: flex-end;
      color: white;
      background-color: #d0d79f;
      background: linear-gradient(to bottom, #949e4e 0%, #949e4e 100%);
      background-attachment: fixed;
      position: relative;
      text-align: right;

      &.last:before {
        content: "";
        position: absolute;
        z-index: 0;
        bottom: 0;
        right: -8px;
        height: 20px;
        width: 20px;
        background: linear-gradient(to bottom, #949e4e 0%, #949e4e 100%);
        background-attachment: fixed;
        border-bottom-left-radius: 15px;
      }

      &.last:after {
        content: "";
        position: absolute;
        z-index: 1;
        bottom: 0;
        right: -10px;
        width: 10px;
        height: 20px;
        background: white;
        border-bottom-left-radius: 10px;
      }
    }

    .type-inline-callback {
      max-width: 90%;
      width: 100%;
    }

    .inline-callback-title {
      padding-bottom: 0.5rem;
    }

    .inline-callback-buttons {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: start;

      a,
      button {
        font-family: $font-text;
        font-weight: 300;
        text-align: center;
        text-decoration: none;
        display: inline-block;
        cursor: pointer;
        font-size: 0.875rem;
        padding: 8px 12px;
        margin: 0;
        color: #6d742c;
        background: #fcfcfc;
        border-radius: calc(8px + 0.875rem);
        border: 1px solid #ccc;

        margin-right: 8px;
        margin-bottom: 8px;

        &:hover,
        &:focus {
          background: #f7f7f7;
        }

        &:last-child {
          margin-right: 0;
        }
      }
    }

    .button-list {
      max-width: 30rem;
      display: flex;
      flex-wrap: wrap;
      flex-direction: column;
      padding: 0;

      .statement-button {
        display: block;
        margin: 0;
        list-style: none;

        a,
        button {
          font-family: $font-text;
          font-weight: 300;
          text-align: center;
          text-decoration: none;
          width: 100%;
          display: block;
          cursor: pointer;
          padding: 8px 1rem;
          margin: 0;
          color: #6d742c;
          background: #fcfcfc;
          border: 1px solid #ccc;
          border-bottom: none;

          &:hover,
          &:focus {
            background: #f7f7f7;
          }
        }

        &:first-child {
          a,
          button {
            border-top-left-radius: 7px;
            border-top-right-radius: 15px;
          }
        }

        &:last-child {
          a,
          button {
            border: 1px solid #ccc;
            border-bottom-left-radius: 15px;
            border-bottom-right-radius: 15px;
          }
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/Bot.vue